import { StatusTag } from '@fountain/fountain-ui-components';
import {
  Avatar,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getInitials } from 'utils/stringutils';

import { messages } from '../messages';
import { EvaluatorStatus, ScorecardEvaluator } from './inviteEvaluators';

const useStyles = makeStyles(theme => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  list: {
    border: theme.customBorders.gray300,
    borderRadius: theme.misc.borderRadius,
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    '&:not(:first-child)': {
      borderTop: theme.customBorders.gray300,
    },
    // StatusTag carries its own 3px radius at the same specificity, so the
    // design's radius nests here to outrank it.
    '& $statusTag': {
      borderRadius: 10,
    },
  },
  avatar: {
    backgroundColor: theme.palette.common.gray200,
    color: theme.palette.common.gray700,
    fontSize: 12,
    height: 32,
    width: 32,
  },
  identity: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
  name: {
    fontWeight: theme.typography.fontWeightBold as number,
  },
  // The theme's caption stays at 14px; the design sets the date a step
  // under the name.
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  statusTag: {},
  removeItem: {
    color: theme.palette.common.red600,
    '&:hover': {
      backgroundColor: theme.palette.common.red100,
    },
  },
}));

const STATUS_MESSAGES = {
  submitted: messages.statusSubmitted,
  draft: messages.statusDraft,
  pending: messages.statusPending,
} as const;

export const StatusPill: VFC<{
  status: EvaluatorStatus;
  className?: string;
}> = ({ status, className }) => {
  const intl = useIntl();
  const theme = useTheme();

  const colors = {
    submitted: {
      color: theme.palette.common.green800,
      backgroundColor: theme.palette.common.green100,
    },
    draft: {
      color: theme.palette.common.blue600,
      backgroundColor: theme.palette.common.blue100,
    },
    pending: {
      color: theme.palette.common.orange800,
      backgroundColor: theme.palette.common.orange100,
    },
  }[status];

  return (
    <StatusTag
      className={className}
      status={intl.formatMessage(STATUS_MESSAGES[status])}
      color={colors.color}
      backgroundColor={colors.backgroundColor}
    />
  );
};

// The row menu every evaluator except the derived default owner carries.
// Send reminder only renders on a remindable row: the reminder rides on a
// stored invitation, so a submitted or stage-assigned evaluator has nothing
// it could send.
const EvaluatorMenu: VFC<{
  userId: string;
  remindable: boolean;
  remindPending: boolean;
  onRemind: (userId: string) => void;
  onRemove: (userId: string) => void;
}> = ({ userId, remindable, remindPending, onRemind, onRemove }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        size="small"
        aria-label={intl.formatMessage(messages.evaluatorActions)}
        onClick={event => setAnchorEl(event.currentTarget)}
        data-testid={`evaluator-menu-${userId}`}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {remindable && (
          <MenuItem
            // One send at a time: a reopened menu cannot fire a second email
            // into the same round trip.
            disabled={remindPending}
            onClick={() => {
              setAnchorEl(null);
              onRemind(userId);
            }}
            data-testid={`remind-evaluator-${userId}`}
          >
            <FormattedMessage {...messages.sendReminder} />
          </MenuItem>
        )}
        <MenuItem
          className={classes.removeItem}
          onClick={() => {
            setAnchorEl(null);
            onRemove(userId);
          }}
          data-testid={`remove-evaluator-${userId}`}
        >
          <FormattedMessage {...messages.removeEvaluator} />
        </MenuItem>
      </Menu>
    </>
  );
};

export interface AssignedEvaluatorsProps {
  evaluators: ScorecardEvaluator[];
  // Whether the viewer may mutate the list. Without it the row menus carry
  // nothing the server would accept, so they are not rendered at all.
  canManage: boolean;
  // True while a reminder is in flight; every Send reminder item disables.
  remindPending: boolean;
  onRemind: (userId: string) => void;
  onRemove: (userId: string) => void;
}

// The evaluators one scorecard instance already carries: submissions,
// drafts, invitations, and the derived default owner, each with a status
// tag and the date they were brought in.
export const AssignedEvaluators: VFC<AssignedEvaluatorsProps> = ({
  evaluators,
  canManage,
  remindPending,
  onRemind,
  onRemove,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  if (evaluators.length === 0) return null;

  const metaLine = (evaluator: ScorecardEvaluator) => {
    const date =
      evaluator.status === 'submitted'
        ? evaluator.submitted_at
        : evaluator.invited_at;
    if (!date) return null;

    const formattedDate = intl.formatDate(date, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return (
      <FormattedMessage
        {...(evaluator.status === 'submitted'
          ? messages.submittedDate
          : messages.invitedDate)}
        values={{ date: formattedDate }}
      />
    );
  };

  return (
    <div className={classes.section} data-testid="assigned-evaluators">
      <Typography variant="body2" color="textSecondary">
        <FormattedMessage {...messages.assignedEvaluators} />
      </Typography>
      <div className={classes.list}>
        {evaluators.map((evaluator, index) => (
          <div
            key={evaluator.user.id ?? index}
            className={classes.row}
            data-testid={`assigned-evaluator-${evaluator.user.id ?? index}`}
          >
            <Avatar className={classes.avatar}>
              {getInitials(evaluator.user.name ?? '')}
            </Avatar>
            <div className={classes.identity}>
              <Typography variant="body2" className={classes.name}>
                {evaluator.user.name}
              </Typography>
              <Typography variant="caption" className={classes.meta}>
                {metaLine(evaluator)}
              </Typography>
            </div>
            <StatusPill
              status={evaluator.status}
              className={classes.statusTag}
            />
            {canManage && !evaluator.default && evaluator.user.id && (
              <EvaluatorMenu
                userId={evaluator.user.id}
                remindable={evaluator.remindable}
                remindPending={remindPending}
                onRemind={onRemind}
                onRemove={onRemove}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
